import { DateTime } from '@ksyos/date-time';
import { Alert, Divider } from '@ksyos/design-system';
import { useSelector } from 'react-redux';
import type { IReduxState } from '../../../../../configureStore';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IAsset } from '../../../../interfaces/asset';
import type { IFileUpload } from '../../../../interfaces/fileUpload';
import type { IEpisodeContext } from '../../../episodes/EpisodeContext';
import type { ICustomControlProcedureReadProps } from '../../interfaces';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { CustomControlStatus } from '../../../../../../common/enums/customControlStatus';
import { DiaryTypes } from '../../../../../../common/enums/diaryTypes';
import { Gender } from '../../../../../../common/enums/gender';
import { HolterFileType } from '../../../../../../common/enums/holterFileType';
import { ProcedureStatus } from '../../../../../../common/enums/procedureStatus';
import { calculateBmi } from '../../../../../../common/utils/bmi';
import { formatEdfMetadata } from '../../../../../../common/utils/formatEdfMetadata';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { analysisReportAssetSchema } from '../../../../../../common/validation/analysisReportAsset';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { getHolterDiaryUrl } from '../../../../../utils/getHolterDiaryUrl';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { emptyValidationErrors, validate } from '../../../../../utils/validation';
import { AssetDropzone } from '../../../../elements/AssetDropzone';
import { AddExaminationButton } from '../../../../elements/carePathButtons/AddExaminationButton';
import { BecomeRequesterButton } from '../../../../elements/carePathButtons/BecomeRequesterButton';
import { CancelProcedureButton } from '../../../../elements/carePathButtons/CancelProcedureButton';
import { EditProcedureButton } from '../../../../elements/carePathButtons/EditProcedureButton';
import { RemoveProcedureButton } from '../../../../elements/carePathButtons/RemoveProcedureButton';
import { TakeAnalysisButton } from '../../../../elements/carePathButtons/TakeAnalysisButton';
import { TakeProcedureAndStartExaminationButton } from '../../../../elements/carePathButtons/TakeProcedureAndStartExaminationButton';
import { TakeProcedureButton } from '../../../../elements/carePathButtons/TakeProcedureButton';
import { TakeSupervisionButton } from '../../../../elements/carePathButtons/TakeSupervisionButton';
import { UpdateSupervisionButton } from '../../../../elements/carePathButtons/UpdateSupervisionButton';
import {
    AnchorButton,
    Button,
    ButtonContainer,
    FormDivider,
    FormParagraph,
    HeaderDivider,
    InfoTooltip,
    UserTextParagraph,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormGroup } from '../../../../elements/forms/FormGroup';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { NullableFormattedDatetime } from '../../../../elements/NullableFormattedDatetime';
import { HolterInstructionModal } from '../../../episodeCategory/customControls/cardiology/holter/customComponents/HolterInstructionModal';
import { ProcedureAssetThumbnails, ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { HolterReadAndUploadDairyModel } from '../../../holters/holterReadAndUploadDairyModel';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { AdviesBijAfwijkendeBeoordelingRead } from './AdviesBijAfwijkendeBeoordelingRead';
import { IndicatieRead } from './IndicatieRead';
import { MedicatieRead } from './MedicatieRead';
import { HolterProcedureType, TcohFollowingSteps } from './TcohFollowingSteps';
import { TcohV2ProcedureSummary } from './TcohV2ProcedureSummary';

interface IEdfMetadata {
    durationSeconds: number;
    endDatetime: string;
    startDatetime: string;
}

interface ITcohV2ProcedureReadState {
    analysisReportAsset: IAsset | null;
    buttonDisabled?: boolean;
    errors: ValidationErrors;
    fileUploadErrorMessage: string | null;
    isBlocked: boolean;
    showDiaryModal: boolean;
    showInstructionsModal: boolean;
}

interface ITcohV2ProcedureReadReduxPropsState {
    organizationId: string;
    practitionerId: string;
}

export function TcohV2ProcedureRead(props: ICustomControlProcedureReadProps) {
    const episodeContext = useEpisodeContext();
    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);

    return (
        <InnerTcohV2ProcedureRead
            {...props}
            organizationId={organizationId}
            practitionerId={practitionerId}
            {...episodeContext}
        />
    );
}

class InnerTcohV2ProcedureRead extends Component<
    ICustomControlProcedureReadProps & ITcohV2ProcedureReadReduxPropsState & IEpisodeContext,
    ITcohV2ProcedureReadState
> {
    constructor(props: ICustomControlProcedureReadProps & ITcohV2ProcedureReadReduxPropsState & IEpisodeContext) {
        super(props);

        this.state = {
            errors: emptyValidationErrors,
            isBlocked: false,
            analysisReportAsset: null,
            fileUploadErrorMessage: null,
            showDiaryModal: false,
            showInstructionsModal: false,
        };
    }

    public render() {
        const buttons = [
            <CancelProcedureButton
                procedure={this.props.procedure}
                removeProcedure={this.props.removeProcedure}
                serviceCode={this.props.request.service.code}
            />,
            <RemoveProcedureButton
                procedure={this.props.procedure}
                removeProcedure={this.props.removeProcedure}
                serviceCode={this.props.request.service.code}
            />,
            <EditProcedureButton
                editProcedure={this.props.editProcedure}
                procedure={this.props.procedure}
            />,
            <TakeProcedureButton
                procedure={this.props.procedure}
                reloadProcess={this.props.reloadProcess}
                request={this.props.request}
            />,
            <BecomeRequesterButton
                procedure={this.props.procedure}
                reloadProcess={this.props.reloadProcess}
                request={this.props.request}
            />,
            <AddExaminationButton
                addExaminationReport={this.props.addExaminationReport}
                request={this.props.request}
            />,
            <TakeProcedureAndStartExaminationButton
                addExaminationReport={this.props.addExaminationReport}
                procedure={this.props.procedure}
                reloadProcess={this.props.reloadProcess}
                request={this.props.request}
            />,
            <TakeSupervisionButton
                practitionerId={this.props.practitionerId}
                reloadProcess={this.props.reloadProcess}
                supervision={this.props.supervision}
                task={this.props.task}
            />,
            <UpdateSupervisionButton
                practitionerId={this.props.practitionerId}
                task={this.props.task}
                updateSupervision={this.props.updateSupervision}
            />,
            <TakeAnalysisButton
                procedure={this.props.procedure}
                reloadProcess={this.props.reloadProcess}
            />,
        ];

        const isPaperDiaryAvailable =
            this.props.procedure.customControlData.diaryType === DiaryTypes.paperUpload &&
            this.props.procedure.customControlData.diaryStorageName;

        if (
            this.props.procedure.status === ProcedureStatus.active &&
            this.props.procedure.customControlStatus === CustomControlStatus.withPatient
        ) {
            buttons.push(
                <AnchorButton
                    color="secondary"
                    large
                    openInNewTab
                    outlined
                    to={getHolterDiaryUrl(this.props.request.id, this.props.patient)}
                >
                    Print dagboek
                </AnchorButton>
            );
        }

        if (this.isAnalyst()) {
            if (this.props.procedure.customControlData.ecgStorageName) {
                buttons.push(
                    <AnchorButton
                        color="secondary"
                        large
                        openInNewTab
                        outlined
                        to={`/api/procedures/${this.props.procedure.id}/holterFile/?holterFileType=${HolterFileType.recording}`}
                    >
                        {translateKey('procedure.downloadRecording')}
                    </AnchorButton>
                );
            }
            if (isPaperDiaryAvailable) {
                buttons.push(
                    <AnchorButton
                        color="secondary"
                        large
                        openInNewTab
                        outlined
                        to={`/api/procedures/${this.props.procedure.id}/holterFile/?holterFileType=${HolterFileType.diary}`}
                    >
                        {translateKey('procedure.downloadDiary')}
                    </AnchorButton>
                );
            }
        }

        if (this.isProcedureInAnalysis()) {
            buttons.push(
                <Button
                    color="secondary"
                    disabled={this.state.analysisReportAsset === null || this.state.buttonDisabled}
                    large
                    onClick={async () => {
                        if (this.state.analysisReportAsset) {
                            this.sendAnalysis(this.state.analysisReportAsset);
                            await this.setState({ fileUploadErrorMessage: null });
                        }
                    }}
                >
                    {translateKey('common.send')}
                </Button>
            );
        }

        if (this.props.procedure.customControlStatus === CustomControlStatus.assetNeeded && this.props.isBiometrist) {
            buttons.push(
                <Button
                    color="secondary"
                    large
                    onClick={async () => await this.setState({ showDiaryModal: true })}
                >
                    Stuur dagboek
                </Button>
            );
        }

        let procedureFiles: IAsset[] = [];
        if (Array.isArray(this.props.procedure.assets)) {
            procedureFiles = procedureFiles.concat(this.props.procedure.assets);
        }

        if (Array.isArray(this.props.procedure.customControlData.files)) {
            procedureFiles = procedureFiles.concat(this.props.procedure.customControlData.files);
        }

        return (
            <>
                <ProcessContainer>
                    <ProcessItemHeader
                        date={this.props.process.procedure && this.props.process.procedure.dateSent}
                        isFirstItem={this.props.isFirstItem}
                        isFirstProcess={this.props.itemState.isFirstProcess}
                        isItemOpen={this.props.itemState.isOpen}
                        onToggle={async (isOpened) =>
                            await this.props.updateItemState({ ...this.props.itemState, isOpen: isOpened })
                        }
                        organizationName={
                            this.props.patient.authorization?.canSeePatientDetails &&
                            this.props.process.procedure?.performerOrganization?.name
                                ? this.props.process.procedure.performerOrganization.name
                                : ''
                        }
                        practitionerName={
                            this.props.patient.authorization &&
                            this.props.patient.authorization.canSeePatientDetails &&
                            this.props.process.procedure &&
                            this.props.process.procedure.performerPractitioner
                                ? getDisplayName(this.props.process.procedure.performerPractitioner)
                                : undefined
                        }
                        process={this.props.process}
                        serviceType={this.props.process.serviceType}
                        showProcessIcon
                        title={getServiceLabel(this.props.process.code, 'procedure', this.props.process.name)}
                    />

                    {this.props.process.isDirectProcedureAdded && <TcohV2ProcedureSummary {...this.props} />}

                    <CustomControlCollapseBody isOpen={this.props.itemState.isOpen}>
                        {this.props.process.isDirectProcedureAdded && (
                            <>
                                <FormDivider />

                                {this.props.patient.gender! !== Gender.male &&
                                    this.props.patient.gender! !== Gender.female &&
                                    this.props.procedure.customControlData.geslachtHolter && (
                                        <HorizontalFormGroupRead>
                                            <FormLabel>Geslacht</FormLabel>
                                            <InputContainer>
                                                <FormParagraph>
                                                    {this.props.procedure.customControlData.geslachtHolter ===
                                                    Gender.male
                                                        ? translateKey('genderEnum.male')
                                                        : translateKey('genderEnum.female')}
                                                </FormParagraph>
                                            </InputContainer>
                                        </HorizontalFormGroupRead>
                                    )}

                                <IndicatieRead customControlData={this.props.procedure.customControlData} />
                                <MedicatieRead customControlData={this.props.procedure.customControlData} />

                                {this.props.procedure.customControlData.hasPacemaker && (
                                    <HorizontalFormGroupRead>
                                        <FormLabel>Pacemaker</FormLabel>
                                        <InputContainer>
                                            <FormParagraph>
                                                {this.props.procedure.customControlData.hasPacemaker}
                                            </FormParagraph>
                                        </InputContainer>
                                    </HorizontalFormGroupRead>
                                )}

                                {this.props.procedure.customControlData.gewicht &&
                                    this.props.procedure.customControlData.lengte && (
                                        <HorizontalFormGroupRead>
                                            <FormLabel>BMI</FormLabel>
                                            <InputContainer>
                                                <FormParagraph>
                                                    {calculateBmi(
                                                        this.props.procedure.customControlData.gewicht,
                                                        this.props.procedure.customControlData.lengte
                                                    )}
                                                </FormParagraph>
                                            </InputContainer>
                                        </HorizontalFormGroupRead>
                                    )}

                                {this.props.procedure.customControlData.bekendeEcgAfwijkingen && (
                                    <HorizontalFormGroupRead>
                                        <FormLabel>Bekende ECG afwijkingen</FormLabel>
                                        <InputContainer>
                                            <UserTextParagraph>
                                                {this.props.procedure.customControlData.bekendeEcgAfwijkingen}
                                            </UserTextParagraph>
                                        </InputContainer>
                                    </HorizontalFormGroupRead>
                                )}
                                <AdviesBijAfwijkendeBeoordelingRead {...this.props.procedure.customControlData} />
                                {this.props.procedure.remark && (
                                    <>
                                        <FormDivider />
                                        <HeaderDivider text={translateKey('common.remarks')} />
                                        <HorizontalFormGroupRead>
                                            <FormLabel>{translateKey('common.remark')}</FormLabel>
                                            <InputContainer>
                                                <UserTextParagraph>{this.props.procedure.remark}</UserTextParagraph>
                                            </InputContainer>
                                        </HorizontalFormGroupRead>
                                    </>
                                )}
                            </>
                        )}

                        {this.props.procedure.id && procedureFiles.length > 0 && (
                            <>
                                {this.renderAlert()}

                                <FormGroup>
                                    <FormLabel>
                                        {translateKey('request.attachment')}: {this.renderNewUploadTooltip()}
                                    </FormLabel>
                                    <ProcedureAssetThumbnails
                                        assets={procedureFiles}
                                        procedureId={this.props.procedure.id}
                                    />
                                </FormGroup>
                            </>
                        )}

                        {this.showRecordingInfoForAnalyst() && (
                            <>
                                <HeaderDivider text="Opname-informatie voor analist" />
                                {this.edfMetadataExists(this.props.procedure.customControlData) && (
                                    <>
                                        {this.props.procedure.customControlData.edfMetadata.length > 1 && (
                                            <Alert appearance="warning">
                                                Let op: er zijn meerdere opnames gemaakt met verschillende start- en
                                                eindtijden. Er kunnen grote tijdsprongen aanwezig zijn tussen de
                                                opnames. Zie onderstaande details per opname.
                                            </Alert>
                                        )}
                                        {this.renderEdfMetadata(this.props.procedure.customControlData.edfMetadata)}
                                    </>
                                )}
                                <HorizontalFormGroupRead>
                                    <FormLabel>ECG aangeboden</FormLabel>
                                    <InputContainer>
                                        <FormParagraph>
                                            <NullableFormattedDatetime
                                                value={this.props.procedure.customControlData.recordingSentDatetime}
                                            />
                                        </FormParagraph>
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            </>
                        )}

                        <Divider />
                        <TcohFollowingSteps holterType={HolterProcedureType.Practice} />
                    </CustomControlCollapseBody>

                    {(this.props.process.isDirectProcedureAdded ||
                        (this.props.procedure.id && procedureFiles.length > 0)) && (
                        <ProcessItemFooter
                            isItemOpen={this.props.itemState.isOpen}
                            labelLess={translateKey('procedure.hideProcedure')}
                            labelMore={translateKey('procedure.showProcedure')}
                            onToggle={async (isOpened) =>
                                await this.props.updateItemState({ ...this.props.itemState, isOpen: isOpened })
                            }
                        />
                    )}

                    {this.props.procedure.customControlStatus === CustomControlStatus.withPatient && (
                        <Alert appearance="info">
                            De recorder is ingesteld. Je kunt deze meegeven aan je patiënt.&nbsp;
                            <Alert.Link
                                onClick={async (e) => {
                                    e.preventDefault();
                                    await this.setState({ showInstructionsModal: true });
                                }}
                                variant="button"
                            >
                                Lees hier de plakinstructies
                            </Alert.Link>
                        </Alert>
                    )}

                    {this.props.procedure.customControlStatus === CustomControlStatus.uploading &&
                        this.props.isBiometrist && (
                            <Alert appearance="info">
                                We versturen de ECG opname naar de analist. Volg de voortgang op{' '}
                                <Alert.Link
                                    to="/holter/dashboard"
                                    variant="link"
                                >
                                    het recorderoverzicht
                                </Alert.Link>
                                .
                            </Alert>
                        )}

                    {this.props.procedure.customControlStatus === CustomControlStatus.analysis &&
                        this.props.isBiometrist && (
                            <Alert appearance="info">
                                De ECG opname is door de analist ontvangen. Binnen enkele dagen ontvangt u de uitslag.
                            </Alert>
                        )}

                    {this.props.procedure.customControlStatus === CustomControlStatus.assetNeeded &&
                        this.props.isBiometrist && (
                            <Alert appearance="warning">
                                We missen het dagboekje. Verstuur dit zo snel mogelijk zodat de analyse kan
                                plaatsvinden.
                            </Alert>
                        )}

                    {this.state.fileUploadErrorMessage && (
                        <Alert appearance="warning">{this.state.fileUploadErrorMessage}</Alert>
                    )}

                    {this.isProcedureInAnalysis() && (
                        <FormGroup>
                            <AssetDropzone
                                acceptedFiles=".pdf"
                                maxFiles={1}
                                onComplete={async (file) => await this.onFileUploadCompleted(file)}
                                onError={async (errorMessage) =>
                                    await this.setState({ fileUploadErrorMessage: errorMessage })
                                }
                                onQueueComplete={async () => await this.setState({ isBlocked: false })}
                                onRemoved={async (file) => await this.onFileUploadRemoved(file)}
                                onSending={async () => {
                                    await this.setState({ isBlocked: true, fileUploadErrorMessage: null });
                                    await fetchJson(
                                        `/api/procedures/${this.props.procedure.id}/customControlData`,
                                        'POST',
                                        {
                                            customControlData: {
                                                addedAnalysisReportDatetime: DateTime.now().toISOString(),
                                            },
                                        }
                                    );
                                }}
                                setMaxAmountFiles={1}
                            />
                        </FormGroup>
                    )}

                    {this.isAnalyst() && !isPaperDiaryAvailable && (
                        <Alert appearance="info"> Geen dagboekje beschikbaar </Alert>
                    )}

                    {this.state.showInstructionsModal && this.renderHolterConfiguredModal()}

                    <ButtonContainer
                        alignRight
                        buttons={this.props.isLastItem ? buttons : []}
                    />
                </ProcessContainer>

                {this.state.showDiaryModal && this.renderDiaryModal()}
            </>
        );
    }

    private showRecordingInfoForAnalyst() {
        return this.canTakeAnalysis() || this.isProcedureInAnalysis();
    }

    private canTakeAnalysis() {
        return (
            this.props.procedure.authorization &&
            (this.props.procedure.authorization.canTakeAnalysis ||
                this.props.procedure.authorization.canTakeAnalysisFromColleague)
        );
    }

    private isAnalyst() {
        return (
            this.props.process.procedure &&
            this.props.process.procedure.holterAnalystPractitionerId === this.props.practitionerId
        );
    }

    private validateAnalysisReportAsset(analysisReportAsset: IAsset) {
        return validate(analysisReportAssetSchema, analysisReportAsset);
    }

    private async onFileUploadCompleted(file: IFileUpload) {
        await this.setState({
            analysisReportAsset: { s3Id: file.s3Id, filename: file.name },
            isBlocked: this.validateAnalysisReportAsset({ s3Id: file.s3Id, filename: file.name }).errors.size > 0,
            fileUploadErrorMessage: null,
        });
    }

    private async onFileUploadRemoved(file: IFileUpload) {
        if (file.s3Id) {
            await this.setState({ analysisReportAsset: null, isBlocked: true, fileUploadErrorMessage: null });
        }
    }

    private isProcedureInAnalysis() {
        return (
            this.props.procedure.status === ProcedureStatus.active &&
            this.props.procedure.customControlStatus === CustomControlStatus.analysis &&
            this.isAnalyst()
        );
    }

    private renderDiaryModal() {
        const recorderType = this.props.procedure.customControlData.recorderType;

        return (
            <HolterReadAndUploadDairyModel
                onClose={async () => {
                    await this.setState({ showDiaryModal: false });
                    if (this.props.reloadProcess) {
                        await this.props.reloadProcess();
                    }
                }}
                recorderType={recorderType}
                requestId={this.props.request.id}
            />
        );
    }

    private async sendAnalysis(analysisAsset: IAsset) {
        await this.setState({ buttonDisabled: true });
        const url = `/api/procedures/${this.props.procedure.id}/sendAnalysis`;
        await fetchJson(url, 'POST', { analysisAsset });
        if (this.props.reloadProcess) {
            await this.setState({ buttonDisabled: false });
            await this.props.reloadProcess();
        }
    }

    private renderEdfMetadata(edfMetadata: IEdfMetadata[]) {
        const result = [];
        for (let i = 0; i < edfMetadata.length; i++) {
            const formattedEdfMetadata = formatEdfMetadata(edfMetadata[i]);
            result.push(
                <HorizontalFormGroupRead key={`edfMetadataHeader-${i}`}>
                    <FormLabel>Recording {i + 1}</FormLabel>
                    <InputContainer>
                        <FormParagraph>Start: {formattedEdfMetadata.start}</FormParagraph>
                    </InputContainer>
                    <InputContainer>
                        <FormParagraph>Eind: {formattedEdfMetadata.end}</FormParagraph>
                    </InputContainer>
                    <InputContainer>
                        <FormParagraph>Totale duur: {formattedEdfMetadata.duration}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            );
        }

        return result;
    }

    private edfMetadataExists(customControlData: any) {
        return (
            customControlData.edfMetadata &&
            customControlData.edfMetadata instanceof Array &&
            customControlData.edfMetadata.length > 0
        );
    }

    private renderHolterConfiguredModal() {
        if (!this.props.request.id) {
            throw new Error('request id undefined');
        }

        const recorderType = this.props.procedure.customControlData.recorderType;

        return (
            <HolterInstructionModal
                holterDiaryUrl={getHolterDiaryUrl(this.props.request.id, this.props.patient)}
                onClose={async () => {
                    await this.setState({ showInstructionsModal: false });
                }}
                recorderType={recorderType}
            />
        );
    }

    private renderNewUploadTooltip() {
        const resendDateTime = this.props.procedure.customControlData.files?.[0].resendDateTime;
        if (
            (this.props.request?.requesterOrganizationId === this.props.organizationId ||
                this.props.procedure?.performerOrganizationId === this.props.organizationId ||
                this.props.patient.careProviderOrganizationId === this.props.organizationId) &&
            resendDateTime
        ) {
            return <InfoTooltip text={this.getNewUploadText(resendDateTime)} />;
        }

        return <></>;
    }

    private renderAlert() {
        const resendDateTime = this.props.procedure.customControlData.files?.[0].resendDateTime;
        if (this.isGraderInCurrentOrganization() && resendDateTime) {
            let alertText = this.getNewUploadText(resendDateTime);
            if (
                this.props.process.diagnosticReports.length &&
                new Date(this.props.request.updated_at).getTime() < new Date(resendDateTime).getTime()
            ) {
                alertText += ' Een eventueel eerder concept van jouw beoordeling hebben we verwijderd.';
            }

            return <Alert appearance="info">{alertText}</Alert>;
        }

        return <></>;
    }

    private isGraderInCurrentOrganization() {
        const currentPractitionerIsInOrganizationRecipient =
            !this.props.process.diagnosticReports?.[0] &&
            this.props.process.recipientOrganizationId === this.props.organizationId;

        const currentPractitionerIsInOrganizationGrader =
            this.props.process.diagnosticReports?.[0] &&
            this.props.process.diagnosticReports[0].performerOrganizationId === this.props.organizationId;

        return currentPractitionerIsInOrganizationRecipient || currentPractitionerIsInOrganizationGrader;
    }

    private getNewUploadText(resendDateTime: Date | string) {
        return `Er heeft op ${DateTime.from(resendDateTime).format(
            'DD-MM-YYYY [om] HH:mm'
        )} een nieuwe upload van het rapport plaatsgevonden. Hier vind je het nieuwste rapport.`;
    }
}
